import { requestJson } from "@/lib/api";

// Módulo de manutenção preventiva da frota. Espelha /api/v1/admin/fleet no backend.
// O status de alerta é sempre resolvido pelo backend (calculado na leitura) — o FE só renderiza.

export type VehicleStatus = "ACTIVE" | "INACTIVE";

export type MaintenanceAlertStatus = "EM_DIA" | "PROXIMA" | "VENCIDA";

export type MaintenanceType =
  | "OLEO_MOTOR"
  | "FILTRO_OLEO"
  | "FILTRO_AR"
  | "FILTRO_COMBUSTIVEL"
  | "PASTILHAS_FREIO"
  | "DISCOS_FREIO"
  | "PNEUS"
  | "ALINHAMENTO_BALANCEAMENTO"
  | "CORREIA_DENTADA"
  | "BATERIA"
  | "REVISAO_GERAL"
  | "OUTROS";

export const MAINTENANCE_TYPE_LABELS: Record<MaintenanceType, string> = {
  OLEO_MOTOR: "Troca de óleo do motor",
  FILTRO_OLEO: "Filtro de óleo",
  FILTRO_AR: "Filtro de ar",
  FILTRO_COMBUSTIVEL: "Filtro de combustível",
  PASTILHAS_FREIO: "Pastilhas de freio",
  DISCOS_FREIO: "Discos de freio",
  PNEUS: "Pneus",
  ALINHAMENTO_BALANCEAMENTO: "Alinhamento e balanceamento",
  CORREIA_DENTADA: "Correia dentada",
  BATERIA: "Bateria",
  REVISAO_GERAL: "Revisão geral",
  OUTROS: "Outros serviços",
};

/** Rótulo de exibição de uma manutenção: nome customizado quando o tipo é "Outros". */
export function maintenanceLabel(type: MaintenanceType, customName?: string | null): string {
  if (type === "OUTROS" && customName?.trim()) return customName.trim();
  return MAINTENANCE_TYPE_LABELS[type];
}

export interface VehicleSummary {
  id: string;
  plate: string;
  brand?: string | null;
  model?: string | null;
  year?: number | null;
  currentMileage: number;
  status: VehicleStatus;
  alertStatus: MaintenanceAlertStatus;
  dueCount: number;
  upcomingCount: number;
}

export interface Maintenance {
  id: string;
  type: MaintenanceType;
  customName?: string | null;
  lastServiceDate?: string | null;
  lastServiceMileage?: number | null;
  intervalKm?: number | null;
  intervalMonths?: number | null;
  nextServiceDate?: string | null;
  nextServiceMileage?: number | null;
  notes?: string | null;
  alertStatus: MaintenanceAlertStatus;
}

export interface VehicleDetail {
  id: string;
  plate: string;
  brand?: string | null;
  model?: string | null;
  year?: number | null;
  currentMileage: number;
  status: VehicleStatus;
  alertStatus: MaintenanceAlertStatus;
  createdAt: string;
  updatedAt: string;
  maintenances: Maintenance[];
}

export interface MileageLog {
  id: string;
  logDate: string;
  mileage: number;
  notes?: string | null;
  recordedBy?: string | null;
  createdAt: string;
}

export interface MaintenanceRecord {
  id: string;
  maintenanceId?: string | null;
  type: MaintenanceType;
  customName?: string | null;
  serviceDate: string;
  mileage: number;
  description?: string | null;
  recordedBy?: string | null;
  cancelled: boolean;
  cancellationReason?: string | null;
  createdAt: string;
}

export interface FleetAlertItem {
  vehicleId: string;
  plate: string;
  brand?: string | null;
  model?: string | null;
  maintenanceId: string;
  type: MaintenanceType;
  customName?: string | null;
  alertStatus: MaintenanceAlertStatus;
  nextServiceDate?: string | null;
  nextServiceMileage?: number | null;
}

export interface FleetOverview {
  totalVehicles: number;
  activeVehicles: number;
  emDia: number;
  proxima: number;
  vencida: number;
  alerts: FleetAlertItem[];
}

export interface AlertConfig {
  alertDaysAhead: number;
  alertKmAhead: number;
}

export interface VehicleInput {
  plate: string;
  brand?: string;
  model?: string;
  year?: number | null;
  currentMileage?: number | null;
  status?: VehicleStatus;
}

export interface MaintenanceInput {
  type: MaintenanceType;
  customName?: string;
  lastServiceDate?: string | null;
  lastServiceMileage?: number | null;
  intervalKm?: number | null;
  intervalMonths?: number | null;
  notes?: string;
}

// ---- Overview + config ------------------------------------------------------------------------

export async function fetchFleetOverview(): Promise<FleetOverview> {
  return requestJson<FleetOverview>("/api/v1/admin/fleet/overview");
}

export async function fetchAlertConfig(): Promise<AlertConfig> {
  return requestJson<AlertConfig>("/api/v1/admin/fleet/alert-config");
}

export async function updateAlertConfig(input: AlertConfig): Promise<AlertConfig> {
  return requestJson<AlertConfig>("/api/v1/admin/fleet/alert-config", { method: "PUT", body: input });
}

// ---- Vehicles ---------------------------------------------------------------------------------

export async function fetchVehicles(): Promise<VehicleSummary[]> {
  return requestJson<VehicleSummary[]>("/api/v1/admin/fleet/vehicles");
}

export async function fetchVehicle(id: string): Promise<VehicleDetail> {
  return requestJson<VehicleDetail>(`/api/v1/admin/fleet/vehicles/${id}`);
}

export async function createVehicle(input: VehicleInput): Promise<VehicleDetail> {
  return requestJson<VehicleDetail>("/api/v1/admin/fleet/vehicles", { method: "POST", body: input });
}

export async function updateVehicle(id: string, input: VehicleInput): Promise<VehicleDetail> {
  return requestJson<VehicleDetail>(`/api/v1/admin/fleet/vehicles/${id}`, { method: "PUT", body: input });
}

// ---- Maintenances -----------------------------------------------------------------------------

export async function addMaintenance(vehicleId: string, input: MaintenanceInput): Promise<VehicleDetail> {
  return requestJson<VehicleDetail>(`/api/v1/admin/fleet/vehicles/${vehicleId}/maintenances`, {
    method: "POST",
    body: input,
  });
}

export async function updateMaintenance(
  vehicleId: string,
  maintenanceId: string,
  input: MaintenanceInput,
): Promise<VehicleDetail> {
  return requestJson<VehicleDetail>(
    `/api/v1/admin/fleet/vehicles/${vehicleId}/maintenances/${maintenanceId}`,
    { method: "PUT", body: input },
  );
}

export async function deleteMaintenance(vehicleId: string, maintenanceId: string): Promise<void> {
  await requestJson<void>(
    `/api/v1/admin/fleet/vehicles/${vehicleId}/maintenances/${maintenanceId}`,
    { method: "DELETE" },
  );
}

export async function registerMaintenance(
  vehicleId: string,
  maintenanceId: string,
  input: { serviceDate: string; mileage: number; description?: string },
): Promise<VehicleDetail> {
  return requestJson<VehicleDetail>(
    `/api/v1/admin/fleet/vehicles/${vehicleId}/maintenances/${maintenanceId}/register`,
    { method: "POST", body: input },
  );
}

// ---- History ----------------------------------------------------------------------------------

export async function fetchHistory(vehicleId: string): Promise<MaintenanceRecord[]> {
  return requestJson<MaintenanceRecord[]>(`/api/v1/admin/fleet/vehicles/${vehicleId}/history`);
}

export async function correctRecord(
  vehicleId: string,
  recordId: string,
  input: { serviceDate: string; mileage: number; description?: string },
): Promise<MaintenanceRecord> {
  return requestJson<MaintenanceRecord>(
    `/api/v1/admin/fleet/vehicles/${vehicleId}/records/${recordId}`,
    { method: "PUT", body: input },
  );
}

export async function cancelRecord(
  vehicleId: string,
  recordId: string,
  reason: string,
): Promise<MaintenanceRecord> {
  return requestJson<MaintenanceRecord>(
    `/api/v1/admin/fleet/vehicles/${vehicleId}/records/${recordId}/cancel`,
    { method: "POST", body: { reason } },
  );
}

// ---- Mileage ----------------------------------------------------------------------------------

export async function addMileage(
  vehicleId: string,
  input: { logDate: string; mileage: number; notes?: string },
): Promise<MileageLog> {
  return requestJson<MileageLog>(`/api/v1/admin/fleet/vehicles/${vehicleId}/mileage`, {
    method: "POST",
    body: input,
  });
}

export async function fetchMileage(vehicleId: string): Promise<MileageLog[]> {
  return requestJson<MileageLog[]>(`/api/v1/admin/fleet/vehicles/${vehicleId}/mileage`);
}
